<template>
  <div class="skill-page">
    <!-- ==================== 头部 ==================== -->
    <div class="header">
      <h1>🌐 正向代理 vs 反向代理</h1>
      <p>
        两者都是<strong>客户端与服务器之间的中间人</strong>，但站位和服务对象完全不同：
        正向代理站在<strong>客户端一侧</strong>，代替客户端对外访问；
        反向代理站在<strong>服务器一侧</strong>，代替服务器接待外部请求。
      </p>
      <div class="header-formula">
        客户端 → <span class="hl-forward">正向代理</span> → <span class="hl-reverse">反向代理</span> → 真实业务服务器
      </div>
    </div>

    <!-- ==================== 一句话核心 ==================== -->
    <div class="core-row">
      <div class="core-card card-forward">
        <div class="core-tag">正向代理 · Forward Proxy</div>
        <h2>站在客户端一侧</h2>
        <p>
          代替<strong>客户端</strong>去访问服务器。<br />
          客户端<strong>知道</strong>自己在用代理，并且要<strong>主动配置</strong>代理地址。
        </p>
        <div class="core-key">
          <span class="key-label">一句话</span>
          <span class="key-text">它代表「我」去跟别人打交道</span>
        </div>
      </div>

      <div class="core-card card-reverse">
        <div class="core-tag">反向代理 · Reverse Proxy</div>
        <h2>站在服务器一侧</h2>
        <p>
          代替<strong>服务器</strong>接待客户端。<br />
          普通用户<strong>完全无感知</strong>，由服务提供方自行部署。
        </p>
        <div class="core-key">
          <span class="key-label">一句话</span>
          <span class="key-text">它代表「服务器」去接待来访者</span>
        </div>
      </div>
    </div>

    <!-- ==================== 交互式链路演示 ==================== -->
    <div class="panel">
      <div class="panel-header">
        <h3>🎬 链路演示 — 请求到底经过了谁？</h3>
        <div class="mode-switch">
          <button
            v-for="m in modeList"
            :key="m.key"
            class="mode-btn"
            :class="{ active: modeKey === m.key }"
            @click="switchMode(m.key)"
          >
            {{ m.label }}
          </button>
        </div>
      </div>

      <div class="track-wrap">
        <div class="flow-track">
          <div
            v-for="(node, i) in nodes"
            :key="node.key"
            class="flow-node"
            :class="[
              'node-' + node.type,
              { active: activeIndex === i, passed: activeIndex > i }
            ]"
          >
            <div class="node-icon">{{ node.icon }}</div>
            <div class="node-title">{{ node.title }}</div>
            <div class="node-sub">{{ node.sub }}</div>
          </div>

          <div
            class="packet"
            :class="[phase, { show: packetVisible }]"
            :style="packetStyle"
          >
            {{ phase === 'request' ? '📨' : '📬' }}
          </div>
        </div>
      </div>

      <div class="demo-controls">
        <button class="btn btn-run" :disabled="running" @click="runDemo">
          {{ running ? '⏳ 传输中…' : '▶ 发送一次请求' }}
        </button>
        <button class="btn btn-ghost" :disabled="running" @click="resetDemo">
          ↺ 重置
        </button>
        <span class="demo-hint">{{ currentMode.tip }}</span>
      </div>

      <div class="log-box" ref="logBox">
        <div
          v-for="(item, i) in logs"
          :key="i"
          class="log-item"
          :class="item.phase"
        >
          <div class="log-line">
            <span class="log-dot"></span>
            <span class="log-title">{{ item.title }}</span>
            <span class="log-node">{{ item.nodeTitle }}</span>
          </div>
          <div class="log-desc">{{ item.desc }}</div>
        </div>
        <div v-if="logs.length === 0" class="log-empty">
          点击「发送一次请求」，观察数据包在链路上的流转过程。
        </div>
      </div>
    </div>

    <!-- ==================== 谁看见了谁 ==================== -->
    <div class="vis-row">
      <div class="vis-card">
        <div class="vis-icon">🖥️</div>
        <div class="vis-title">目标服务器看到</div>
        <div class="vis-main vis-forward">正向代理的 IP</div>
        <div class="vis-note">真实用户身份被隐藏</div>
      </div>
      <div class="vis-card">
        <div class="vis-icon">👤</div>
        <div class="vis-title">客户端看到</div>
        <div class="vis-main vis-reverse">反向代理的域名</div>
        <div class="vis-note">后端有几台机器完全不知道</div>
      </div>
      <div class="vis-card">
        <div class="vis-icon">🗄️</div>
        <div class="vis-title">后端业务服务器看到</div>
        <div class="vis-main vis-reverse">反向代理的 IP</div>
        <div class="vis-note">只信任来自代理的请求</div>
      </div>
    </div>

    <!-- ==================== 对比表格 ==================== -->
    <div class="section">
      <h3 class="section-title">⚖️ 核心区别对照表</h3>
      <div class="table-wrap">
        <table class="compare-table">
          <thead>
            <tr>
              <th class="col-dim">对比维度</th>
              <th class="th-forward">正向代理</th>
              <th class="th-reverse">反向代理</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="col-dim">服务对象</td>
              <td>服务<strong>客户端</strong></td>
              <td>服务<strong>服务器</strong></td>
            </tr>
            <tr>
              <td class="col-dim">隐蔽方向</td>
              <td>隐藏<strong>客户端</strong></td>
              <td>隐藏<strong>服务器</strong></td>
            </tr>
            <tr>
              <td class="col-dim">配置方</td>
              <td>客户端主动设置</td>
              <td>服务方部署</td>
            </tr>
            <tr>
              <td class="col-dim">部署位置</td>
              <td>客户端侧 / 企业内网出口</td>
              <td>服务端入口 / 机房边缘</td>
            </tr>
            <tr>
              <td class="col-dim">知晓方</td>
              <td>客户端知道自己在用代理</td>
              <td>普通用户完全无感知</td>
            </tr>
            <tr>
              <td class="col-dim">典型场景</td>
              <td>企业统一出口、跨区域访问、爬虫采集</td>
              <td>负载均衡、安全隔离、缓存加速、CDN</td>
            </tr>
            <tr>
              <td class="col-dim">常见实现</td>
              <td>Squid、Clash、公司上网网关</td>
              <td>Nginx、HAProxy、LVS、CDN 边缘节点</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="section-tip">
        本质区别只有一条：<strong>服务对象不同</strong>，其余差异都由它衍生。
      </p>
    </div>

    <!-- ==================== 混合链路 ==================== -->
    <div class="section">
      <h3 class="section-title">🔗 同一条链路上可以同时有两种代理吗？</h3>
      <div class="mix-card">
        <div class="mix-flow">
          <span class="mix-node mix-client">客户端</span>
          <span class="mix-arrow">→</span>
          <span class="mix-node mix-forward">正向代理</span>
          <span class="mix-arrow">→</span>
          <span class="mix-node mix-reverse">反向代理</span>
          <span class="mix-arrow">→</span>
          <span class="mix-node mix-server">真实业务服务器</span>
        </div>
        <p class="mix-desc">
          可以，<strong>两段代理各司其职，互不冲突</strong>。<br />
          典型例子：员工通过公司的正向代理访问某个大型网站，而这个网站前面还有一层反向代理。
          正向代理代表「用户出网」，反向代理代表「网站接客」，谁也不知道对方的存在，也不需要知道。
        </p>
      </div>
    </div>

    <!-- ==================== 快速判断 ==================== -->
    <div class="section">
      <h3 class="section-title">🧭 怎么快速判断代理类型？</h3>
      <div class="judge-tip">
        只要问一句：<strong>它代表谁去跟谁打交道？</strong>
        <span class="judge-item judge-forward">代表客户端访问服务器 → 正向代理</span>
        <span class="judge-item judge-reverse">代表服务器接待客户端 → 反向代理</span>
      </div>

      <div class="quiz-list">
        <div
          v-for="(q, i) in quizList"
          :key="i"
          class="quiz-card"
          :class="{ open: quizOpen[i] }"
          @click="toggleQuiz(i)"
        >
          <div class="quiz-q">
            <span class="quiz-index">{{ i + 1 }}</span>
            <span class="quiz-text">{{ q.scene }}</span>
            <span class="quiz-toggle">{{ quizOpen[i] ? '−' : '+' }}</span>
          </div>
          <div v-show="quizOpen[i]" class="quiz-a">
            <span class="quiz-answer" :class="q.answerClass">{{ q.answer }}</span>
            <span class="quiz-reason">{{ q.reason }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== 排查思路 ==================== -->
    <div class="section">
      <h3 class="section-title">🛠️ 出问题了怎么排查？</h3>
      <p class="section-sub">
        先判断<strong>故障影响范围</strong>，就能瞬间缩小一半排查范围。
      </p>

      <div class="trouble-row">
        <div class="trouble-card t-forward">
          <div class="trouble-head">
            <span class="trouble-badge badge-forward">正向代理故障</span>
            <span class="trouble-scope">影响范围：特定用户 / 特定网络</span>
          </div>
          <ul>
            <li>客户端代理地址、端口是否写对</li>
            <li>代理认证凭据是否过期、是否需要账号密码</li>
            <li>PAC 规则 / 例外名单是否把目标站点漏掉了</li>
            <li>代理出口 IP 是否被目标站点风控封禁</li>
            <li>DNS 解析发生在客户端还是代理侧</li>
          </ul>
        </div>

        <div class="trouble-card t-reverse">
          <div class="trouble-head">
            <span class="trouble-badge badge-reverse">反向代理故障</span>
            <span class="trouble-scope">影响范围：整个网站 / 全部用户</span>
          </div>
          <ul>
            <li>upstream 后端节点是否存活、端口是否通</li>
            <li>Nginx error.log 中的 upstream 报错</li>
            <li>502 / 504 与超时、连接数、缓冲区配置</li>
            <li>证书、SNI 与 HTTPS 卸载配置</li>
            <li>负载均衡策略、会话保持与健康检查</li>
          </ul>
        </div>
      </div>

      <div class="table-wrap">
        <table class="trouble-table">
          <thead>
            <tr>
              <th>典型症状</th>
              <th>更可能是</th>
              <th>先查什么</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>只有部分用户访问慢 / 打不开</td>
              <td><span class="pill pill-forward">正向代理</span></td>
              <td>客户端代理配置、出口带宽</td>
            </tr>
            <tr>
              <td>访问被目标站点拒绝（403）</td>
              <td><span class="pill pill-forward">正向代理</span></td>
              <td>代理出口 IP 是否被风控</td>
            </tr>
            <tr>
              <td>只有某几个用户报证书错误</td>
              <td><span class="pill pill-forward">正向代理</span></td>
              <td>代理是否做了 HTTPS 中间人解密</td>
            </tr>
            <tr>
              <td>全站 502 / 504</td>
              <td><span class="pill pill-reverse">反向代理</span></td>
              <td>upstream 后端是否存活</td>
            </tr>
            <tr>
              <td>静态资源很快、动态接口很慢</td>
              <td><span class="pill pill-reverse">反向代理</span></td>
              <td>缓存规则与回源链路</td>
            </tr>
            <tr>
              <td>流量明显倾斜到某一台后端</td>
              <td><span class="pill pill-reverse">反向代理</span></td>
              <td>负载均衡策略与权重配置</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ==================== 配置示例 ==================== -->
    <div class="section">
      <h3 class="section-title">📄 两种代理的最小配置示例</h3>
      <div class="code-row">
        <div class="code-card">
          <div class="code-card-head">正向代理 — 客户端主动指定</div>
          <div class="code-block">
            <span class="cm"># curl 通过代理访问（HTTPS 会自动走 CONNECT）</span><br />
            <span class="fn">curl</span> -x http://127.0.0.1:7890 https://example.com<br /><br />
            <span class="cm"># 环境变量方式，大量命令行工具都认</span><br />
            <span class="kw">export</span> http_proxy=http://127.0.0.1:7890<br />
            <span class="kw">export</span> https_proxy=http://127.0.0.1:7890<br /><br />
            <span class="cm"># 浏览器则在系统/扩展中配置代理地址或 PAC 脚本</span>
          </div>
        </div>

        <div class="code-card">
          <div class="code-card-head">反向代理 — 服务方部署（Nginx）</div>
          <div class="code-block">
            <span class="cm"># 负载均衡 + 隐藏后端 + TLS 卸载</span><br />
            <span class="kw">upstream</span> backend {<br />
            &nbsp;&nbsp;server 10.0.0.11:8080 <span class="nu">weight=3</span>;<br />
            &nbsp;&nbsp;server 10.0.0.12:8080;<br />
            }<br /><br />
            <span class="kw">server</span> {<br />
            &nbsp;&nbsp;listen <span class="nu">443</span> ssl;<br />
            &nbsp;&nbsp;server_name example.com;<br /><br />
            &nbsp;&nbsp;<span class="kw">location</span> / {<br />
            &nbsp;&nbsp;&nbsp;&nbsp;proxy_pass http://backend;<br />
            &nbsp;&nbsp;&nbsp;&nbsp;proxy_set_header Host $host;<br />
            &nbsp;&nbsp;&nbsp;&nbsp;proxy_set_header X-Real-IP $remote_addr;<br />
            &nbsp;&nbsp;&nbsp;&nbsp;proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;<br />
            &nbsp;&nbsp;}<br />
            }
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== 总结 ==================== -->
    <div class="section">
      <div class="summary-card">
        <h3>💡 搞懂这个区别的实际用处</h3>
        <p>
          能帮你<strong>快速定位网络问题，缩小排查范围</strong>：<br />
          正向代理出问题 → 影响<strong>特定用户</strong>，排查客户端配置；<br />
          反向代理出问题 → 影响<strong>整个网站</strong>，排查服务端配置。<br />
          常见表象无非这四类：网络变慢、访问被拒、证书报错、负载不均 —— 先问「它代表谁」，答案就出来了。
        </p>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 正向代理 / 反向代理 知识点演示页
 * 三条链路（仅正向、仅反向、两者串联）共用一套节点渲染 + 数据包动画。
 */

const MODES = {
  forward: {
    label: '只有正向代理',
    tip: '客户端主动配置代理，目标服务器看不到真实用户。',
    nodes: [
      { key: 'client', icon: '💻', title: '客户端', sub: '真实用户', type: 'client' },
      { key: 'fproxy', icon: '🚪', title: '正向代理', sub: '代表客户端出网', type: 'forward' },
      { key: 'server', icon: '🖥️', title: '目标服务器', sub: '只看到代理 IP', type: 'server' }
    ],
    steps: [
      {
        i: 0,
        phase: 'request',
        title: '客户端发起请求',
        desc: '客户端在系统或浏览器里主动配置了代理地址，请求并不直接发往目标站点，而是先交给代理。'
      },
      {
        i: 1,
        phase: 'request',
        title: '请求先到正向代理',
        desc: 'HTTP 代理会收到完整目标 URL，HTTPS 代理则收到 CONNECT 目标，由代理代替客户端出网。'
      },
      {
        i: 2,
        phase: 'request',
        title: '代理转发到目标服务器',
        desc: '目标服务器看到的来源 IP 是代理的 IP，真实用户身份被隐藏。'
      },
      {
        i: 2,
        phase: 'response',
        title: '目标服务器返回响应',
        desc: '服务器把响应交回给正向代理 —— 它始终以为自己只是在跟代理对话。'
      },
      {
        i: 1,
        phase: 'response',
        title: '代理回传给客户端',
        desc: '正向代理把响应交还给客户端，一次「代表客户端」的访问完成。'
      }
    ]
  },
  reverse: {
    label: '只有反向代理',
    tip: '用户只知道一个域名，后端架构被完全隐藏。',
    nodes: [
      { key: 'client', icon: '👤', title: '客户端', sub: '普通用户，无感知', type: 'client' },
      { key: 'rproxy', icon: '🛡️', title: '反向代理', sub: '代表服务器接待', type: 'reverse' },
      { key: 'server', icon: '🗄️', title: '后端服务器', sub: '藏在内网', type: 'server' }
    ],
    steps: [
      {
        i: 0,
        phase: 'request',
        title: '客户端请求一个域名',
        desc: '用户只知道一个公网域名，完全不知道背后有几台服务器、跑在什么端口上。'
      },
      {
        i: 1,
        phase: 'request',
        title: '反向代理按规则转发',
        desc: '反向代理在这里做负载均衡、TLS 卸载、缓存判断，再决定把请求交给哪台后端。'
      },
      {
        i: 2,
        phase: 'request',
        title: '后端服务器处理业务',
        desc: '后端只接受来自反向代理的请求，通常不直接对外网开放，攻击者第一跳只能撞到代理。'
      },
      {
        i: 2,
        phase: 'response',
        title: '后端返回处理结果',
        desc: '业务处理完成，结果先回到反向代理。'
      },
      {
        i: 1,
        phase: 'response',
        title: '反向代理返回给客户端',
        desc: '静态内容甚至可能不经过后端，直接由反向代理或它的缓存层返回。'
      }
    ]
  },
  both: {
    label: '两者串联',
    tip: '两段代理各司其职，互不冲突，也互不需要知道对方存在。',
    nodes: [
      { key: 'client', icon: '💻', title: '客户端', sub: '真实用户', type: 'client' },
      { key: 'fproxy', icon: '🚪', title: '正向代理', sub: '客户端侧', type: 'forward' },
      { key: 'rproxy', icon: '🛡️', title: '反向代理', sub: '服务器侧', type: 'reverse' },
      { key: 'server', icon: '🗄️', title: '业务服务器', sub: '最内层', type: 'server' }
    ],
    steps: [
      {
        i: 0,
        phase: 'request',
        title: '客户端发起请求',
        desc: '用户所在网络要求统一走公司出口，客户端配置了正向代理。'
      },
      {
        i: 1,
        phase: 'request',
        title: '正向代理代为出网',
        desc: '正向代理代表客户端访问外网，目标网站看到的是正向代理的出口 IP。'
      },
      {
        i: 2,
        phase: 'request',
        title: '请求抵达网站反向代理',
        desc: '对网站而言，正向代理只是「一个普通访客」，反向代理照常按规则分发请求。'
      },
      {
        i: 3,
        phase: 'request',
        title: '反向代理转发到业务服务器',
        desc: '真实业务服务器藏在最内层：它既不认识用户，也不认识那个正向代理。'
      },
      {
        i: 3,
        phase: 'response',
        title: '业务服务器返回结果',
        desc: '结果开始逐层回传。'
      },
      {
        i: 2,
        phase: 'response',
        title: '反向代理回传',
        desc: '反向代理可能顺带把响应缓存下来，供后续访客复用。'
      },
      {
        i: 1,
        phase: 'response',
        title: '正向代理回传',
        desc: '正向代理把结果交还给客户端。'
      },
      {
        i: 0,
        phase: 'response',
        title: '客户端收到响应',
        desc: '整条链路上两段代理各司其职，互不冲突 —— 这就是「同一条链路两种代理」的样子。'
      }
    ]
  }
}

const QUIZ_LIST = [
  {
    scene: '公司要求所有员工上网都走同一台网关，对外只暴露一个公司出口 IP。',
    answer: '正向代理',
    answerClass: 'ans-forward',
    reason: '它代表客户端出网，隐藏的是真实用户身份。'
  },
  {
    scene: '网站把 10 台后端服务器藏在 Nginx 后面，用户只访问一个域名。',
    answer: '反向代理',
    answerClass: 'ans-reverse',
    reason: '它代表服务器接客，隐藏的是真实后端架构。'
  },
  {
    scene: '爬虫程序配置 proxy 参数轮换 IP，避免被目标站点封禁。',
    answer: '正向代理',
    answerClass: 'ans-forward',
    reason: '客户端主动配置，目标是隐藏自己的来源 IP。'
  },
  {
    scene: 'CDN 边缘节点缓存静态资源，让用户就近拿到文件。',
    answer: '反向代理',
    answerClass: 'ans-reverse',
    reason: '边缘节点就是反向代理，用户无感知，缓存加速是它的核心能力。'
  },
  {
    scene: '客户端 → 公司正向代理 → 网站反向代理 → 真实业务服务器。',
    answer: '两者都有',
    answerClass: 'ans-both',
    reason: '两段代理串联，各自代表不同的一方，互不冲突。'
  }
]

export default {
  name: 'ForwardReverseProxy',

  data() {
    return {
      modeKey: 'forward',
      modeList: [
        { key: 'forward', label: '只有正向代理' },
        { key: 'reverse', label: '只有反向代理' },
        { key: 'both', label: '两者串联' }
      ],
      quizList: QUIZ_LIST,
      quizOpen: {},

      // --- 动画状态 ---
      activeIndex: -1,
      packetIndex: 0,
      packetVisible: false,
      phase: 'request',
      logs: [],
      running: false,

      destroyed: false
    }
  },

  computed: {
    currentMode() {
      return MODES[this.modeKey]
    },

    nodes() {
      return this.currentMode.nodes
    },

    packetStyle() {
      const count = this.nodes.length || 1
      const left = ((this.packetIndex + 0.5) / count) * 100
      return { left: left + '%' }
    }
  },

  beforeDestroy() {
    this.destroyed = true
  },

  methods: {
    switchMode(key) {
      if (this.running || this.modeKey === key) return
      this.modeKey = key
      this.resetDemo()
    },

    resetDemo() {
      this.activeIndex = -1
      this.packetIndex = 0
      this.packetVisible = false
      this.phase = 'request'
      this.logs = []
      this.running = false
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    pushLog(step) {
      const node = this.nodes[step.i]
      this.logs.push({
        phase: step.phase,
        title: step.title,
        desc: step.desc,
        nodeTitle: node ? node.title : ''
      })
      this.$nextTick(() => {
        const box = this.$refs.logBox
        if (box) {
          box.scrollTop = box.scrollHeight
        }
      })
    },

    async runDemo() {
      if (this.running) return

      this.running = true
      this.logs = []
      this.activeIndex = -1
      this.packetIndex = 0
      this.packetVisible = false

      const steps = this.currentMode.steps

      for (let k = 0; k < steps.length; k++) {
        if (this.destroyed) return

        const step = steps[k]
        this.phase = step.phase
        this.packetIndex = step.i
        this.activeIndex = step.i
        this.packetVisible = true
        this.pushLog(step)

        await this.sleep(900)
      }

      if (this.destroyed) return
      this.running = false
    },

    toggleQuiz(index) {
      this.$set(this.quizOpen, index, !this.quizOpen[index])
    }
  }
}
</script>

<style scoped>
/* ==================== 头部 ==================== */
.header {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: #fff;
  padding: 32px 20px;
  text-align: center;
  border-radius: 12px;
  margin-bottom: 24px;
}

.header h1 {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 2px;
  margin: 0;
}

.header p {
  margin: 10px auto 0;
  font-size: 14px;
  opacity: .88;
  max-width: 720px;
  line-height: 1.8;
}

.header-formula {
  display: inline-block;
  margin-top: 16px;
  padding: 8px 18px;
  border-radius: 30px;
  background: rgba(255, 255, 255, .12);
  border: 1px solid rgba(255, 255, 255, .22);
  font-size: 13px;
  letter-spacing: .5px;
}

.hl-forward { color: #7ec8ff; font-weight: 600; }
.hl-reverse { color: #86efac; font-weight: 600; }

/* ==================== 一句话核心 ==================== */
.core-row {
  display: flex;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto 24px;
}

.core-card {
  flex: 1;
  background: var(--surface-1);
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--shadow-surface-1);
  border-top: 4px solid #ddd;
}

.card-forward { border-top-color: #1890ff; }
.card-reverse { border-top-color: #52c41a; }

.core-tag {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  margin-bottom: 12px;
}

.card-forward .core-tag { background: #e8f4fd; color: #1890ff; }
.card-reverse .core-tag { background: #f6ffed; color: #52c41a; }

.core-card h2 {
  font-size: 19px;
  margin: 0 0 10px;
  color: #333;
}

.core-card p {
  font-size: 14px;
  line-height: 1.85;
  color: #555;
  margin: 0;
}

.core-key {
  margin-top: 16px;
  padding: 10px 14px;
  border-radius: 8px;
  background: var(--surface-1);
  display: flex;
  align-items: center;
  gap: 10px;
}

.key-label {
  font-size: 12px;
  color: #999;
  flex-shrink: 0;
}

.key-text {
  font-size: 13px;
  font-weight: 600;
  color: #444;
}

/* ==================== 演示面板 ==================== */
.panel {
  max-width: 1200px;
  margin: 0 auto 24px;
  background: var(--surface-1);
  border-radius: 12px;
  box-shadow: var(--shadow-surface-1);
  padding: 22px 24px 24px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 18px;
}

.panel-header h3 {
  font-size: 17px;
  margin: 0;
  color: #333;
}

.mode-switch {
  display: flex;
  gap: 8px;
  background: var(--surface-2);
  padding: 4px;
  border-radius: 10px;
}

.mode-btn {
  border: none;
  background: transparent;
  padding: 7px 16px;
  font-size: 13px;
  color: #666;
  border-radius: 7px;
  cursor: pointer;
  transition: all .2s;
}

.mode-btn:hover { color: #1e3c72; }

.mode-btn.active {
  background: var(--surface-1);
  color: #1e3c72;
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .12);
}

.track-wrap {
  overflow-x: auto;
  padding: 54px 0 12px;
}

.flow-track {
  position: relative;
  display: flex;
  min-width: 640px;
}

.flow-node {
  flex: 1;
  position: relative;
  text-align: center;
  padding: 14px 8px;
  border-radius: 10px;
  border: 2px solid #eee;
  background: var(--surface-1);
  margin-right: 34px;
  transition: all .3s;
}

.flow-node:last-child { margin-right: 0; }

.flow-node:not(:last-child)::after {
  content: '➜';
  position: absolute;
  right: -27px;
  top: 50%;
  transform: translateY(-50%);
  color: #c8c8c8;
  font-size: 16px;
}

.node-icon { font-size: 24px; line-height: 1.2; }

.node-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-top: 6px;
}

.node-sub {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.flow-node.passed { border-color: #d6e4ff; background: #f7fbff; }

.flow-node.active {
  border-color: #1e3c72;
  background: #eef4ff;
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(30, 60, 114, .16);
}

.flow-node.node-forward.active { border-color: #1890ff; background: #e8f4fd; }
.flow-node.node-reverse.active { border-color: #52c41a; background: #f6ffed; }

.packet {
  position: absolute;
  top: -44px;
  transform: translateX(-50%);
  font-size: 22px;
  opacity: 0;
  transition: left .55s cubic-bezier(.4, 0, .2, 1), opacity .25s;
  pointer-events: none;
}

.packet.show { opacity: 1; }

.packet.request { filter: drop-shadow(0 0 6px rgba(24, 144, 255, .6)); }
.packet.response { filter: drop-shadow(0 0 6px rgba(82, 196, 26, .6)); }

.demo-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.btn {
  border: none;
  border-radius: 8px;
  padding: 10px 22px;
  font-size: 14px;
  cursor: pointer;
  transition: all .2s;
}

.btn-run {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: #fff;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(30, 60, 114, .3);
}

.btn-run:hover:not(:disabled) { transform: translateY(-1px); }

.btn-run:disabled,
.btn-ghost:disabled { opacity: .55; cursor: not-allowed; }

.btn-ghost {
  background: var(--surface-2);
  color: #666;
}

.btn-ghost:hover:not(:disabled) { background: #eceef5; }

.demo-hint {
  font-size: 12px;
  color: #aaa;
  flex: 1;
  min-width: 200px;
}

.log-box {
  margin-top: 16px;
  max-height: 260px;
  overflow-y: auto;
  background: var(--surface-1);
  border-radius: 10px;
  padding: 10px 14px;
}

.log-box::-webkit-scrollbar { width: 4px; }
.log-box::-webkit-scrollbar-thumb { background: #ddd; border-radius: 2px; }

.log-item {
  padding: 9px 0;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
}

.log-item:last-child { border-bottom: none; }

.log-line {
  display: flex;
  align-items: center;
  gap: 8px;
}

.log-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.log-item.request .log-dot { background: #1890ff; }
.log-item.response .log-dot { background: #52c41a; }

.log-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #333;
}

.log-node {
  font-size: 11px;
  color: #999;
  background: var(--surface-2);
  padding: 1px 8px;
  border-radius: 10px;
}

.log-desc {
  font-size: 12.5px;
  color: #888;
  line-height: 1.7;
  margin-top: 3px;
  padding-left: 15px;
}

.log-empty {
  font-size: 13px;
  color: #bbb;
  padding: 14px 0;
  text-align: center;
}

/* ==================== 谁看见了谁 ==================== */
.vis-row {
  display: flex;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto 24px;
}

.vis-card {
  flex: 1;
  background: var(--surface-1);
  border-radius: 12px;
  padding: 18px 20px;
  box-shadow: var(--shadow-surface-1);
  text-align: center;
}

.vis-icon { font-size: 22px; }

.vis-title {
  font-size: 13px;
  color: #888;
  margin-top: 6px;
}

.vis-main {
  font-size: 16px;
  font-weight: 700;
  margin-top: 6px;
}

.vis-forward { color: #1890ff; }
.vis-reverse { color: #52c41a; }

.vis-note {
  font-size: 12px;
  color: #bbb;
  margin-top: 4px;
}

/* ==================== 通用区块 ==================== */
.section {
  max-width: 1200px;
  margin: 0 auto 24px;
}

.section-title {
  font-size: 19px;
  color: #333;
  margin: 0 0 14px;
}

.section-sub {
  font-size: 13px;
  color: #888;
  margin: -6px 0 14px;
}

.section-tip {
  font-size: 13px;
  color: #888;
  margin-top: 12px;
}

.table-wrap {
  background: var(--surface-1);
  border-radius: 12px;
  box-shadow: var(--shadow-surface-1);
  overflow: hidden;
}

.compare-table,
.trouble-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
}

.compare-table th,
.compare-table td,
.trouble-table th,
.trouble-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
}

.compare-table thead th,
.trouble-table thead th {
  background: var(--surface-1);
  font-size: 13px;
  color: #666;
  font-weight: 600;
}

.compare-table tbody tr:last-child td,
.trouble-table tbody tr:last-child td { border-bottom: none; }

.compare-table tbody tr:hover { background: #fafbff; }

.col-dim { color: #888; width: 140px; }

.th-forward { color: #1890ff; }
.th-reverse { color: #52c41a; }

.compare-table td:nth-child(2) { color: #3178c6; }
.compare-table td:nth-child(3) { color: #389e0d; }

/* ==================== 混合链路 ==================== */
.mix-card {
  background: var(--surface-1);
  border-radius: 12px;
  box-shadow: var(--shadow-surface-1);
  padding: 24px;
}

.mix-flow {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 8px 0 18px;
}

.mix-node {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
}

.mix-client { background: #f0f0f0; color: #555; }
.mix-forward { background: #e8f4fd; color: #1890ff; }
.mix-reverse { background: #f6ffed; color: #52c41a; }
.mix-server { background: #fff3e6; color: #d46b08; }

.mix-arrow { color: #c8c8c8; }

.mix-desc {
  font-size: 14px;
  line-height: 1.9;
  color: #555;
  margin: 0;
}

/* ==================== 快速判断 ==================== */
.judge-tip {
  background: var(--surface-1);
  border-radius: 12px;
  box-shadow: var(--shadow-surface-1);
  padding: 18px 22px;
  font-size: 14px;
  color: #444;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 16px;
}

.judge-item {
  font-size: 13px;
  padding: 5px 14px;
  border-radius: 20px;
  font-weight: 600;
}

.judge-forward { background: #e8f4fd; color: #1890ff; }
.judge-reverse { background: #f6ffed; color: #52c41a; }

.quiz-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.quiz-card {
  background: var(--surface-1);
  border-radius: 10px;
  box-shadow: var(--shadow-surface-1);
  padding: 14px 18px;
  cursor: pointer;
  transition: all .2s;
  border: 1px solid transparent;
}

.quiz-card:hover { border-color: #dbe4f5; }

.quiz-card.open { border-color: #1e3c72; }

.quiz-q {
  display: flex;
  align-items: center;
  gap: 12px;
}

.quiz-index {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #eef2fb;
  color: #1e3c72;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.quiz-text {
  flex: 1;
  font-size: 13.5px;
  color: #444;
  line-height: 1.6;
}

.quiz-toggle {
  font-size: 18px;
  color: #bbb;
  flex-shrink: 0;
}

.quiz-a {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #eee;
  padding-left: 34px;
}

.quiz-answer {
  display: inline-block;
  font-size: 12.5px;
  font-weight: 700;
  padding: 3px 12px;
  border-radius: 20px;
  margin-right: 10px;
}

.ans-forward { background: #e8f4fd; color: #1890ff; }
.ans-reverse { background: #f6ffed; color: #52c41a; }
.ans-both { background: #f9f0ff; color: #722ed1; }

.quiz-reason {
  font-size: 13px;
  color: #888;
  line-height: 1.7;
}

/* ==================== 排查 ==================== */
.trouble-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.trouble-card {
  flex: 1;
  background: var(--surface-1);
  border-radius: 12px;
  box-shadow: var(--shadow-surface-1);
  padding: 20px 22px;
  border-left: 4px solid #ddd;
}

.t-forward { border-left-color: #1890ff; }
.t-reverse { border-left-color: #52c41a; }

.trouble-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.trouble-badge {
  font-size: 13px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 20px;
}

.badge-forward { background: #e8f4fd; color: #1890ff; }
.badge-reverse { background: #f6ffed; color: #52c41a; }

.trouble-scope {
  font-size: 12px;
  color: #999;
}

.trouble-card ul {
  margin: 0;
  padding-left: 18px;
}

.trouble-card li {
  font-size: 13.5px;
  color: #555;
  line-height: 2;
}

.trouble-table td:nth-child(2) { width: 130px; }

.pill {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 20px;
}

.pill-forward { background: #e8f4fd; color: #1890ff; }
.pill-reverse { background: #f6ffed; color: #52c41a; }

/* ==================== 配置示例 ==================== */
.code-row {
  display: flex;
  gap: 20px;
}

.code-card {
  flex: 1;
  background: var(--surface-1);
  border-radius: 12px;
  box-shadow: var(--shadow-surface-1);
  overflow: hidden;
}

.code-card-head {
  padding: 14px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  background: var(--surface-1);
}

.code-card .code-block {
  border-radius: 0;
  margin: 0;
  min-height: 100%;
}

.code-block {
  background: #1e1e2e;
  color: #cdd6f4;
  padding: 20px 22px;
  font-size: 13px;
  line-height: 1.9;
  overflow-x: auto;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', monospace;
}

.code-block .kw { color: #cba6f7; }
.code-block .fn { color: #89b4fa; }
.code-block .cm { color: #6c7086; }
.code-block .nu { color: #fab387; }

/* ==================== 总结 ==================== */
.summary-card {
  background: linear-gradient(135deg, #f7f9ff 0%, #eef4ff 100%);
  border: 1px solid #dbe4f5;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 40px;
}

.summary-card h3 {
  font-size: 17px;
  color: #1e3c72;
  margin: 0 0 12px;
}

.summary-card p {
  font-size: 14px;
  line-height: 2;
  color: #555;
  margin: 0;
}

/* ==================== 响应式 ==================== */
@media (max-width: 900px) {
  .core-row,
  .vis-row,
  .trouble-row,
  .code-row {
    flex-direction: column;
  }

  .panel-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
